import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";
import axios from "axios";

import { API_URL, getPosSession } from "./api";

import "./CompanyBranding.css";

const CompanyBrandingContext = createContext(null);

const DEFAULT_BRANDING = {
  company_name: "",
  commercial_name: "",
  business_name: "",
  logo: "",
  favicon: "",
  primary_color: "#1f5d7a",
  secondary_color: "#16445b",
  login_background_color: "#eef2ff",
};

function getSafeColor(color, fallback) {
  const value = typeof color === "string" ? color.trim() : "";

  return /^#[0-9A-F]{6}$/i.test(value)
    ? value
    : fallback;
}

function getPublicAssetUrl(path) {
  if (typeof path !== "string" || !path.trim()) {
    return "";
  }

  try {
    const backend = new URL(API_URL, window.location.origin);
    const asset = new URL(path.trim(), `${backend.origin}/`);

    return ["http:", "https:"].includes(asset.protocol)
      ? asset.href
      : "";
  } catch {
    return "";
  }
}

// Selecciona únicamente datos visuales de la sesión.
// No incluye el token en el contexto de branding.
function getStoredCompanyInfo(company) {
  const session = getPosSession(company);

  if (!session) return {};

  return {
    company_name: session.company_name || "",
    commercial_name: session.commercial_name || "",
    business_name: session.business_name || "",
    logo: session.logo || session.company_logo || session.logo_url || "",
    favicon: session.favicon || "",
    primary_color: session.primary_color,
    secondary_color: session.secondary_color,
    login_background_color: session.login_background_color,
  };
}

function normalizeBranding(data = {}, storedInfo = {}) {
  const source = {
    ...DEFAULT_BRANDING,
    ...storedInfo,
    ...data,
  };

  return {
    ...source,
    company_name:
      source.company_name ||
      source.business_name ||
      source.commercial_name ||
      "",
    commercial_name: source.commercial_name || "",
    business_name: source.business_name || "",
    logo: source.logo || "",
    favicon: source.favicon || "",
    primary_color: getSafeColor(
      source.primary_color,
      DEFAULT_BRANDING.primary_color
    ),
    secondary_color: getSafeColor(
      source.secondary_color,
      DEFAULT_BRANDING.secondary_color
    ),
    login_background_color: getSafeColor(
      source.login_background_color,
      DEFAULT_BRANDING.login_background_color
    ),
  };
}

export function CompanyBrandingProvider({ children }) {
  const { company } = useParams();

  const requestRef = useRef(null);

  const [brandingState, setBrandingState] = useState(() => ({
    company,
    data: normalizeBranding({}, getStoredCompanyInfo(company)),
  }));

  const [loadingBranding, setLoadingBranding] = useState(false);
  const [brandingError, setBrandingError] = useState("");

  const branding = useMemo(
    () =>
      brandingState.company === company
        ? brandingState.data
        : normalizeBranding({}, getStoredCompanyInfo(company)),
    [brandingState, company]
  );

  const applyBranding = useCallback(
    (data = {}) => {
      setBrandingState({
        company,
        data: normalizeBranding(
          data,
          getStoredCompanyInfo(company)
        ),
      });
    },
    [company]
  );

  const loadBranding = useCallback(
    async (companySlug = company) => {
      // Este proveedor pertenece a la empresa de la ruta actual.
      if (!companySlug || companySlug !== company) {
        return;
      }

      requestRef.current?.abort();

      const controller = new AbortController();
      requestRef.current = controller;

      setLoadingBranding(true);
      setBrandingError("");

      try {
        const response = await axios.get(
          `${API_URL}/companies/by-subdomain/${encodeURIComponent(
            companySlug
          )}/branding`,
          {
            signal: controller.signal,
          }
        );

        if (!controller.signal.aborted) {
          applyBranding(response.data);
        }
      } catch (error) {
        if (controller.signal.aborted) return;

        const detail = error.response?.data?.detail;

        setBrandingError(
          typeof detail === "string"
            ? detail
            : "No fue posible cargar la identidad visual."
        );

        applyBranding({});
      } finally {
        if (!controller.signal.aborted) {
          setLoadingBranding(false);
        }

        if (requestRef.current === controller) {
          requestRef.current = null;
        }
      }
    },
    [applyBranding, company]
  );

  useEffect(() => {
    applyBranding({});
    loadBranding();

    return () => {
      requestRef.current?.abort();
    };
  }, [applyBranding, loadBranding]);

  // Aplica y restaura las variables, el título y el favicon.
  useEffect(() => {
    const root = document.documentElement;

    const variables = {
      "--company-primary": branding.primary_color,
      "--company-secondary": branding.secondary_color,
      "--company-primary-dark": branding.secondary_color,
      "--company-login-background": branding.login_background_color,
    };

    const previousVariables = Object.keys(variables).map((name) => ({
      name,
      value: root.style.getPropertyValue(name),
      priority: root.style.getPropertyPriority(name),
    }));

    Object.entries(variables).forEach(([name, value]) => {
      root.style.setProperty(name, value);
    });

    const previousTitle = document.title;

    const companyName =
      branding.commercial_name ||
      branding.business_name ||
      branding.company_name;

    document.title = companyName
      ? `${companyName} | NetLedger POS`
      : "NetLedger POS";

    let favicon = document.querySelector("link[rel~='icon']");
    const previousHref = favicon?.getAttribute("href") ?? null;
    let createdFavicon = false;

    const faviconUrl = getPublicAssetUrl(branding.favicon);

    if (faviconUrl) {
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
        createdFavicon = true;
      }

      favicon.href = faviconUrl;
    }

    return () => {
      previousVariables.forEach(({ name, value, priority }) => {
        if (value) {
          root.style.setProperty(name, value, priority);
        } else {
          root.style.removeProperty(name);
        }
      });

      document.title = previousTitle;

      if (faviconUrl && favicon) {
        if (createdFavicon) {
          favicon.remove();
        } else if (previousHref !== null) {
          favicon.setAttribute("href", previousHref);
        } else {
          favicon.removeAttribute("href");
        }
      }
    };
  }, [branding]);

  const value = useMemo(
    () => ({
      branding,
      companyName:
        branding.commercial_name ||
        branding.business_name ||
        branding.company_name ||
        "Empresa",
      loadingBranding,
      brandingError,
      getPublicAssetUrl,
      applyBranding,
      refreshBranding: loadBranding,
    }),
    [
      branding,
      loadingBranding,
      brandingError,
      applyBranding,
      loadBranding,
    ]
  );

  return (
    <CompanyBrandingContext.Provider value={value}>
      {children}
    </CompanyBrandingContext.Provider>
  );
}

export function useCompanyBranding() {
  const context = useContext(CompanyBrandingContext);

  if (!context) {
    throw new Error(
      "useCompanyBranding debe usarse dentro de CompanyBrandingProvider."
    );
  }

  return context;
}